import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Typography, Button, Chip, Box, IconButton, Drawer, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { Menu as MenuIcon, Add, People as PeopleIcon, Assessment, BarChart, Close } from '@mui/icons-material';
import '../index.css';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  Event as CalendarIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { getCounsellorStatus, getStatusChipStyles, CounsellorJourneyStatus } from '../utils/counsellorUtils';
import { getClientStatus, getClientStatusChipStyles, ClientJourneyStatus } from '../utils/clientUtils';
import ThemeBackground from '../components/Layout/ThemeBackground';
import ClientCard from '../components/Mobile/ClientCard';
import BottomNav from '../components/Mobile/BottomNav';
import ListFilters from '../components/common/ListFilters';


interface Client {
  _id: string;
  firstName: string;
  lastName: string;
  phone: string;
  referralCategory: 'direct' | 'organisation';
  referralType: string;
  organisation?: string;
  organisationDisplayName?: string;
  numberOfSessions?: number;
  sessionRate?: number;
  createdAt: string;
  updatedAt: string;
  ClientRef?: string;
  assessed?: boolean;
  onboarded?: boolean;
  assignedCounsellor?: string;
  assignedCounsellorName?: string;
  email?: string;
  referralTypeDisplayName?: string;
  termsAccepted?: boolean;
}

interface Counsellor {
  _id: string;
  firstName: string;
  lastName: string;
  applicationStatus: 'Registered Interest' | 'Pending' | 'Started' | 'Submitted' | 'Interview Scheduled' | 'Interview Completed' | 'Legal Info Requested' | 'Legal Info Update Requested' | 'Legal Info Submitted' | 'Legal Info Approved' | 'Legal Info Rejected' | 'Accepted' | 'Rejected' | 'Ready For Onboarding' | 'Onboarding Review' | 'Pending Agreement' | 'Active' | 'Pending Self-Completion' | 'Suspended';
  onboardingStatus?: 'Not Started' | 'In Progress' | 'Completed';
  createdAt: string;
  updatedAt: string;
  user?: {
    _id: string;
    email: string;
    role: string;
  };
}

const Home: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [counsellors, setCounsellors] = useState<Counsellor[]>([]);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'clients' | 'counsellors'>('clients');
  const [searchQuery, setSearchQuery] = useState('');
  // Filter/sort preferences persist across refreshes via localStorage.
  const [clientStatusFilter, setClientStatusFilter] = useState(() => localStorage.getItem('home.clientStatusFilter') || 'all');
  const [counsellorStatusFilter, setCounsellorStatusFilter] = useState(() => localStorage.getItem('home.counsellorStatusFilter') || 'all');
  const [clientSortBy, setClientSortBy] = useState(() => localStorage.getItem('home.clientSortBy') || 'newest');
  const [counsellorSortBy, setCounsellorSortBy] = useState(() => localStorage.getItem('home.counsellorSortBy') || 'newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const itemsPerPage = 10;
  const { user, logout } = useAuth();

  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Check URL parameters to set initial tab
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'counsellors') {
      setActiveTab('counsellors');
    }
  }, [searchParams]);

  // Persist filter/sort choices so they survive page refreshes
  useEffect(() => {
    localStorage.setItem('home.clientStatusFilter', clientStatusFilter);
    localStorage.setItem('home.counsellorStatusFilter', counsellorStatusFilter);
    localStorage.setItem('home.clientSortBy', clientSortBy);
    localStorage.setItem('home.counsellorSortBy', counsellorSortBy);
  }, [clientStatusFilter, counsellorStatusFilter, clientSortBy, counsellorSortBy]);

  const fetchClients = async () => {
    try {
      const response = await api.get('/api/clients');
      console.log('Clients response:', response.data);
      if (response.data) {
        const validClients = response.data.filter((client: Client) => client !== null);
        setClients(validClients);
      }
    } catch (error: any) {
      console.error('Error fetching clients:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      }
      setClients([]);
    }
  };

  const fetchCounsellors = async () => {
    try {
      const response = await api.get('/api/counsellors');
      console.log('Counsellors response:', response.data);
      if (response.data) {
        const validCounsellors = response.data.filter((counsellor: Counsellor) => counsellor !== null);
        setCounsellors(validCounsellors);
      }
    } catch (error: any) {
      console.error('Error fetching counsellors:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      }
      setCounsellors([]);
    }
  };

  const fetchAllData = useCallback(async () => {
    await Promise.all([fetchClients(), fetchCounsellors()]);
  }, []);

  // Initial load (no loading state needed for Home page)
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Auto-refresh every 30 seconds (silent, no loading state)
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchAllData();
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [fetchAllData]);

  // Filter and sort clients
  const filteredClients = useMemo(() => {
    let result = [...clients];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(client => 
        `${client.firstName} ${client.lastName}`.toLowerCase().includes(query) ||
        client.email?.toLowerCase().includes(query) ||
        client.phone?.toLowerCase().includes(query) ||
        client.referralType?.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (clientStatusFilter !== 'all') {
      result = result.filter(client => {
        const status = getClientStatus(client as any);
        switch (clientStatusFilter) {
          case 'awaiting_onboarding':
            return status === 'Awaiting Onboarding';
          case 'pending_contract':
            return status === 'Pending Contract';
          case 'awaiting_counsellor':
            return status === 'Awaiting Counsellor';
          case 'assigned':
            return status === 'Assigned';
          case 'awaiting_appointment':
            return status === 'Awaiting Appointment';
          case 'awaiting_session_notes':
            return status === 'Awaiting Session Notes';
          case 'pending_archive':
            return status === 'Pending Archive';
          case 'completed':
            return status === 'Completed';
          case 'cancelled':
            return status === 'Cancelled';
          case 'archived':
            return status === 'Archived';
          default:
            return true;
        }
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      switch (clientSortBy) {
        case 'newest':
          return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        case 'oldest':
          return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
        case 'name_asc':
          return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        case 'name_desc':
          return `${b.firstName} ${b.lastName}`.localeCompare(`${a.firstName} ${a.lastName}`);
        default:
          return 0;
      }
    });

    return result;
  }, [clients, searchQuery, clientStatusFilter, clientSortBy]);

  // Filter and sort counsellors
  const filteredCounsellors = useMemo(() => {
    let result = [...counsellors];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(counsellor => 
        `${counsellor.firstName} ${counsellor.lastName}`.toLowerCase().includes(query) ||
        counsellor.user?.email?.toLowerCase().includes(query)
      );
    }

    // Apply status filter based on display status
    if (counsellorStatusFilter !== 'all') {
      result = result.filter(counsellor => {
        const displayStatus = getCounsellorStatus(counsellor as any);
        switch (counsellorStatusFilter) {
          case 'registered_interest':
            return displayStatus === 'Registered Interest';
          case 'application_not_started':
            return displayStatus === 'Application Not Started';
          case 'application_in_progress':
            return displayStatus === 'Application in Progress';
          case 'application_under_review':
            return displayStatus === 'Application Under Review';
          case 'interview_scheduled':
            return displayStatus === 'Interview Scheduled';
          case 'interview_completed':
            return displayStatus === 'Interview Completed';
          case 'ready_for_onboarding':
            return displayStatus === 'Ready for Onboarding';
          case 'pending_agreement':
            return displayStatus === 'Pending Agreement';
          case 'pending_self_completion':
            return displayStatus === 'Pending Self-Completion';
          case 'active':
            return displayStatus === 'Active Counsellor';
          case 'suspended':
            return displayStatus === 'Suspended';
          case 'rejected':
            return displayStatus === 'Rejected';
          case 'archived':
            return displayStatus === 'Archived';
          default:
            return true;
        }
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      switch (counsellorSortBy) {
        case 'newest':
          return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        case 'oldest':
          return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
        case 'name_asc':
          return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        case 'name_desc':
          return `${b.firstName} ${b.lastName}`.localeCompare(`${a.firstName} ${a.lastName}`);
        default:
          return 0;
      }
    });

    return result;
  }, [counsellors, searchQuery, counsellorStatusFilter, counsellorSortBy]);

  // Calculate filter counts for clients
  const clientFilterCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: clients.length,
      awaiting_onboarding: 0,
      pending_contract: 0,
      awaiting_counsellor: 0,
      assigned: 0,
      awaiting_appointment: 0,
      awaiting_session_notes: 0,
      pending_archive: 0,
      completed: 0,
      cancelled: 0,
      archived: 0,
    };

    clients.forEach(client => {
      const status = getClientStatus(client as any);
      switch (status) {
        case 'Awaiting Onboarding':
          counts.awaiting_onboarding++;
          break;
        case 'Pending Contract':
          counts.pending_contract++;
          break;
        case 'Awaiting Counsellor':
          counts.awaiting_counsellor++;
          break;
        case 'Assigned':
          counts.assigned++;
          break;
        case 'Awaiting Appointment':
          counts.awaiting_appointment++;
          break;
        case 'Awaiting Session Notes':
          counts.awaiting_session_notes++;
          break;
        case 'Pending Archive':
          counts.pending_archive++;
          break;
        case 'Completed':
          counts.completed++;
          break;
        case 'Cancelled':
          counts.cancelled++;
          break;
        case 'Archived':
          counts.archived++;
          break;
      }
    });

    return counts;
  }, [clients]);

  // Calculate filter counts for counsellors based on display status
  const counsellorFilterCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: counsellors.length,
      registered_interest: 0,
      application_not_started: 0,
      application_in_progress: 0,
      application_under_review: 0,
      interview_scheduled: 0,
      interview_completed: 0,
      ready_for_onboarding: 0,
      pending_agreement: 0,
      pending_self_completion: 0,
      active: 0,
      suspended: 0,
      rejected: 0,
      archived: 0,
    };

    counsellors.forEach(counsellor => {
      const displayStatus = getCounsellorStatus(counsellor as any);
      switch (displayStatus) {
        case 'Registered Interest':
          counts.registered_interest++;
          break;
        case 'Application Not Started':
          counts.application_not_started++;
          break;
        case 'Application in Progress':
          counts.application_in_progress++;
          break;
        case 'Application Under Review':
          counts.application_under_review++;
          break;
        case 'Interview Scheduled':
          counts.interview_scheduled++;
          break;
        case 'Interview Completed':
          counts.interview_completed++;
          break;
        case 'Ready for Onboarding':
          counts.ready_for_onboarding++;
          break;
        case 'Pending Agreement':
          counts.pending_agreement++;
          break;
        case 'Pending Self-Completion':
          counts.pending_self_completion++;
          break;
        case 'Active Counsellor':
          counts.active++;
          break;
        case 'Suspended':
          counts.suspended++;
          break;
        case 'Rejected':
          counts.rejected++;
          break;
        case 'Archived':
          counts.archived++;
          break;
      }
    });

    return counts;
  }, [counsellors]);

  const isClient = (item: Client | Counsellor): item is Client => 'referralCategory' in item;

  // Pagination logic
  const totalItems = activeTab === 'clients' ? filteredClients.length : filteredCounsellors.length;
  const totalPages = Math.max(Math.ceil(totalItems / itemsPerPage), 1);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = activeTab === 'clients'
    ? filteredClients.slice(indexOfFirstItem, indexOfLastItem)
    : filteredCounsellors.slice(indexOfFirstItem, indexOfLastItem);

  const handleTabSwitch = (tab: 'clients' | 'counsellors') => {
    setActiveTab(tab);
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleClientFilterChange = (value: string) => {
    setClientStatusFilter(value);
    setCurrentPage(1);
  };

  const handleCounsellorFilterChange = (value: string) => {
    setCounsellorStatusFilter(value);
    setCurrentPage(1);
  };

  const handleClientSortChange = (value: string) => {
    setClientSortBy(value);
    setCurrentPage(1);
  };

  const handleCounsellorSortChange = (value: string) => {
    setCounsellorSortBy(value);
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <ThemeBackground variant="full">
      {/* Mobile Header */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, pb: '80px' }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            px: 2,
            py: 1.5
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 600,
                fontSize: '1.25rem'
              }}
            >
              Clients
            </Typography>
            <IconButton
              onClick={() => setMobileMenuOpen(true)}
              sx={{
                color: 'white',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                width: 40,
                height: 40,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a4293 100%)',
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Mobile Search */}
        <Box sx={{ px: 2, py: 2, background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="theme-input w-full"
            style={{
              padding: '12px 16px',
              fontSize: '16px',
              borderRadius: '8px'
            }}
          />
        </Box>

        {/* Mobile Client Cards */}
        <Box sx={{ px: 2, py: 2 }}>
          {filteredClients.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'rgba(255, 255, 255, 0.5)'
              }}
            >
              <Typography variant="body1">
                No clients found
              </Typography>
            </Box>
          ) : (
            filteredClients.map((client) => (
              <ClientCard
                key={client._id}
                client={client}
                getClientStatus={getClientStatus}
                getClientStatusChipStyles={getClientStatusChipStyles}
              />
            ))
          )}
        </Box>

        {/* Mobile Menu Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          PaperProps={{
            sx: {
              width: { xs: '85%', sm: 320 },
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              borderLeft: '1px solid rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                p: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  Admin Menu
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Admin'}
                </Typography>
              </Box>
              <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: 'white' }}>
                <Close />
              </IconButton>
            </Box>

            <List sx={{ flex: 1, pt: 2 }}>
              <ListItem
                button
                onClick={() => { navigate('/onboarding'); setMobileMenuOpen(false); }}
                sx={{ py: 2 }}
              >
                <ListItemIcon><Add sx={{ color: '#2196f3' }} /></ListItemIcon>
                <ListItemText
                  primary="Add Client"
                  primaryTypographyProps={{ sx: { color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 } }}
                />
              </ListItem>
              <ListItem
                button
                onClick={() => { navigate('/counsellor-management'); setMobileMenuOpen(false); }}
                sx={{ py: 2 }}
              >
                <ListItemIcon><PeopleIcon sx={{ color: '#4caf50' }} /></ListItemIcon>
                <ListItemText
                  primary="Counsellor Management"
                  primaryTypographyProps={{ sx: { color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 } }}
                />
              </ListItem>
              <ListItem
                button
                onClick={() => { navigate('/assessor'); setMobileMenuOpen(false); }}
                sx={{ py: 2 }}
              >
                <ListItemIcon><Assessment sx={{ color: '#9c27b0' }} /></ListItemIcon>
                <ListItemText
                  primary="Assessor Page"
                  primaryTypographyProps={{ sx: { color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 } }}
                />
              </ListItem>
              <ListItem
                button
                onClick={() => { navigate('/admin/utilisation'); setMobileMenuOpen(false); }}
                sx={{ py: 2 }}
              >
                <ListItemIcon><BarChart sx={{ color: '#14b8a6' }} /></ListItemIcon>
                <ListItemText
                  primary="Utilisation Report"
                  primaryTypographyProps={{ sx: { color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 } }}
                />
              </ListItem>
              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              <ListItem
                button
                onClick={() => { navigate('/calendar'); setMobileMenuOpen(false); }}
                sx={{ py: 2 }}
              >
                <ListItemIcon><CalendarIcon sx={{ color: '#5c6bc0' }} /></ListItemIcon>
                <ListItemText
                  primary="Calendar"
                  primaryTypographyProps={{ sx: { color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 } }}
                />
              </ListItem>
              <ListItem
                button
                onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                sx={{ py: 2 }}
              >
                <ListItemIcon><DashboardIcon sx={{ color: '#00796b' }} /></ListItemIcon>
                <ListItemText
                  primary="Admin Panel"
                  primaryTypographyProps={{ sx: { color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 } }}
                />
              </ListItem>
              <ListItem
                button
                onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }}
                sx={{ py: 2 }}
              >
                <ListItemIcon><SettingsIcon sx={{ color: '#6366f1' }} /></ListItemIcon>
                <ListItemText
                  primary="Settings"
                  primaryTypographyProps={{ sx: { color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 } }}
                />
              </ListItem>
            </List>
          </Box>
        </Drawer>

        {/* Bottom Navigation */}
        <BottomNav />
      </Box>

      {/* Desktop View */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
      <div className="container mx-auto px-6 py-8 overflow-y-scroll">
        <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="glass-card-light mb-6">
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-start">
                {/* Buttons on the left */}
                <div className="flex space-x-4">
                  <Button
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minWidth: '160px',
                      backgroundColor: '#2196f3',
                      '&:hover': {
                        backgroundColor: '#1976d2'
                      }
                    }}
                    onClick={() => navigate('/onboarding')}
                  >
                    Add Client
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minWidth: '160px',
                      backgroundColor: '#4caf50',
                      '&:hover': {
                        backgroundColor: '#388e3c'
                      }
                    }}
                    onClick={() => navigate('/counsellor-management')}
                  >
                    Counsellor Management
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minWidth: '160px',
                      backgroundColor: '#9c27b0',
                      '&:hover': {
                        backgroundColor: '#7b1fa2'
                      }
                    }}
                    onClick={() => navigate('/assessor')}
                  >
                    Assessor Page
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minWidth: '160px',
                      backgroundColor: '#14b8a6',
                      '&:hover': {
                        backgroundColor: '#0d9488'
                      }
                    }}
                    onClick={() => navigate('/admin/utilisation')}
                  >
                    Utilisation Report
                  </Button>
                </div>

                {/* Title and user info on the right */}
                <div className="text-right">
                  <Typography 
                    variant="h3" 
                    component="h1" 
                    gutterBottom
                    className="theme-text-primary"
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 800,
                      fontSize: '2.5rem',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Standard Practice
                  </Typography>
                  <Typography 
                    variant="subtitle1" 
                    className="theme-text-secondary"
                    sx={{
                      fontWeight: 500,
                      fontSize: '1.1rem'
                    }}
                  >
                    {user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Admin User' : 'Admin User'}
                  </Typography>
                </div>
              </div>
            </div>
          </div>

          {/* Admin buttons in a separate row */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex space-x-4">
              <Button
                variant="contained"
                startIcon={<CalendarIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: '120px',
                  backgroundColor: '#5c6bc0',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#3949ab'
                  }
                }}
                onClick={() => navigate('/calendar')}
              >
                Calendar
              </Button>
              <Button
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: '120px',
                  backgroundColor: '#00796b',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#00695c'
                  }
                }}
                onClick={() => navigate('/admin')}
              >
                Admin Panel
              </Button>
              <Button
                variant="contained"
                startIcon={<DashboardIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: '120px',
                  backgroundColor: '#2e7d32',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1b5e20'
                  }
                }}
                onClick={() => navigate('/admin/dashboard')}
              >
                Dashboard
              </Button>
              <Button
                variant="contained"
                startIcon={<AccountBalanceIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: '120px',
                  backgroundColor: '#c62828',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#b71c1c'
                  }
                }}
                onClick={() => navigate('/mtd')}
              >
                Tax Digital
              </Button>
              <Button
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: '120px',
                  backgroundColor: '#795548',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#5d4037'
                  }
                }}
                onClick={() => navigate('/archived-records')}
              >
                Archived Records
              </Button>
              <Button
                variant="contained"
                startIcon={<SettingsIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: '120px',
                  backgroundColor: '#6366f1',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#4f46e5'
                  }
                }}
                onClick={() => navigate('/settings')}
              >
                Settings
              </Button>
              <Button
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: '120px',
                  backgroundColor: '#d32f2f',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#c62828'
                  }
                }}
                onClick={handleLogout}
              >
                Logout
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b border-white/20">
            <div className="flex space-x-4">
              <button
                onClick={() => handleTabSwitch('clients')}
                className={`px-4 py-2 font-medium transition-all duration-200 ${
                  activeTab === 'clients'
                    ? 'border-b-2 border-indigo-400 text-white'
                    : 'text-blue-200 hover:text-white'
                }`}
              >
                Clients
              </button>
              <button
                onClick={() => handleTabSwitch('counsellors')}
                className={`px-4 py-2 font-medium transition-all duration-200 ${
                  activeTab === 'counsellors'
                    ? 'border-b-2 border-indigo-400 text-white'
                    : 'text-blue-200 hover:text-white'
                }`}
              >
                Counsellors
              </button>
            </div>
          </div>

          {/* Filters */}
          {activeTab === 'clients' ? (
            <ListFilters
              searchValue={searchQuery}
              onSearchChange={handleSearchChange}
              searchPlaceholder="Search clients by name, email, phone, or referral type..."
              filterValue={clientStatusFilter}
              onFilterChange={handleClientFilterChange}
              filterOptions={[
                { value: 'all', label: 'All Clients', count: clientFilterCounts.all },
                { value: 'awaiting_onboarding', label: 'Awaiting Onboarding', count: clientFilterCounts.awaiting_onboarding },
                { value: 'pending_contract', label: 'Pending Contract', count: clientFilterCounts.pending_contract },
                { value: 'awaiting_counsellor', label: 'Awaiting Counsellor', count: clientFilterCounts.awaiting_counsellor },
                { value: 'assigned', label: 'Assigned', count: clientFilterCounts.assigned },
                { value: 'awaiting_appointment', label: 'Awaiting Appointment', count: clientFilterCounts.awaiting_appointment },
                { value: 'awaiting_session_notes', label: 'Awaiting Session Notes', count: clientFilterCounts.awaiting_session_notes },
                { value: 'pending_archive', label: 'Pending Archive', count: clientFilterCounts.pending_archive },
                { value: 'completed', label: 'Completed', count: clientFilterCounts.completed },
                { value: 'cancelled', label: 'Cancelled', count: clientFilterCounts.cancelled },
                { value: 'archived', label: 'Archived', count: clientFilterCounts.archived },
              ]}
              filterLabel="Filter by Status"
              sortValue={clientSortBy}
              onSortChange={handleClientSortChange}
              sortOptions={[
                { value: 'newest', label: 'Newest First' },
                { value: 'oldest', label: 'Oldest First' },
                { value: 'name_asc', label: 'Name (A-Z)' },
                { value: 'name_desc', label: 'Name (Z-A)' },
              ]}
              sortLabel="Sort by"
              totalCount={clients.length}
              filteredCount={filteredClients.length}
            />
          ) : (
            <ListFilters
              searchValue={searchQuery}
              onSearchChange={handleSearchChange}
              searchPlaceholder="Search counsellors by name or email..."
              filterValue={counsellorStatusFilter}
              onFilterChange={handleCounsellorFilterChange}
              filterOptions={[
                { value: 'all', label: 'All Counsellors', count: counsellorFilterCounts.all },
                { value: 'registered_interest', label: 'Registered Interest', count: counsellorFilterCounts.registered_interest },
                { value: 'application_not_started', label: 'Application Not Started', count: counsellorFilterCounts.application_not_started },
                { value: 'application_in_progress', label: 'Application in Progress', count: counsellorFilterCounts.application_in_progress },
                { value: 'application_under_review', label: 'Application Under Review', count: counsellorFilterCounts.application_under_review },
                { value: 'interview_scheduled', label: 'Interview Scheduled', count: counsellorFilterCounts.interview_scheduled },
                { value: 'interview_completed', label: 'Interview Completed', count: counsellorFilterCounts.interview_completed },
                { value: 'ready_for_onboarding', label: 'Ready for Onboarding', count: counsellorFilterCounts.ready_for_onboarding },
                { value: 'pending_agreement', label: 'Pending Agreement', count: counsellorFilterCounts.pending_agreement },
                { value: 'pending_self_completion', label: 'Pending Self-Completion', count: counsellorFilterCounts.pending_self_completion },
                { value: 'active', label: 'Active Counsellor', count: counsellorFilterCounts.active },
                { value: 'suspended', label: 'Suspended', count: counsellorFilterCounts.suspended },
                { value: 'rejected', label: 'Rejected', count: counsellorFilterCounts.rejected },
                { value: 'archived', label: 'Archived', count: counsellorFilterCounts.archived },
              ]}
              filterLabel="Filter by Status"
              sortValue={counsellorSortBy}
              onSortChange={handleCounsellorSortChange}
              sortOptions={[
                { value: 'newest', label: 'Newest First' },
                { value: 'oldest', label: 'Oldest First' },
                { value: 'name_asc', label: 'Name (A-Z)' },
                { value: 'name_desc', label: 'Name (Z-A)' },
              ]}
              sortLabel="Sort by"
              totalCount={counsellors.length}
              filteredCount={filteredCounsellors.length}
            />
          )}

          {/* Table */}
          <div className="overflow-x-auto glass-card">
            <table className="min-w-full">
              <thead className="glass-card-light">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                    Name
                  </th>
                  {activeTab === 'clients' && (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                        Client Ref
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                        Referral Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                        Assigned Counsellor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                        Status
                      </th>
                    </>
                  )}
                  {activeTab === 'counsellors' && (
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                      Status
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {currentItems.map((item) => (
                  <tr key={item._id} className="hover:bg-white/5 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium theme-text-primary">
                        {item.firstName} {item.lastName}
                      </div>
                    </td>
                    {isClient(item) ? (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm theme-text-muted">
                            {item.ClientRef || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm theme-text-muted">
                            {item.referralCategory === 'organisation' 
                              ? item.organisationDisplayName 
                              : item.referralTypeDisplayName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm theme-text-muted">
                            {item.assignedCounsellorName || 'Not Assigned'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <Chip
                              label={getClientStatus(item)}
                              sx={{
                                height: '24px',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                ...getClientStatusChipStyles(item)
                              }}
                            />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/client-details/${item._id}`);
                            }}
                            className="theme-button-secondary text-xs px-2 py-1"
                          >
                            View
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <Chip
                              label={getCounsellorStatus(item)}
                              sx={{
                                height: '24px',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                ...getStatusChipStyles(item)
                              }}
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/counsellors/${item._id}`);
                            }}
                            className="theme-button-secondary text-xs px-2 py-1"
                          >
                            View
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-4 px-6 py-3 glass-card-light border-t border-white/10">
            <div className="flex-1 flex justify-between sm:hidden space-x-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md transition-all duration-200 ${
                  currentPage === 1
                    ? 'border-white/20 theme-text-muted cursor-not-allowed'
                    : 'theme-button-secondary'
                }`}
              >
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md transition-all duration-200 ${
                  currentPage === totalPages
                    ? 'border-white/20 theme-text-muted cursor-not-allowed'
                    : 'theme-button-secondary'
                }`}
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm theme-text-secondary">
                  Showing{' '}
                  <span className="font-medium theme-text-primary">{indexOfFirstItem + 1}</span>
                  {' - '}
                  <span className="font-medium theme-text-primary">
                    {Math.min(indexOfLastItem, totalItems)}
                  </span>{' '}
                  of <span className="font-medium theme-text-primary">{totalItems}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex space-x-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-3 py-2 rounded-md border text-sm font-medium transition-all duration-200 ${
                      currentPage === 1
                        ? 'border-white/20 theme-text-muted cursor-not-allowed glass-card-light'
                        : 'theme-button-secondary'
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-3 py-2 rounded-md border text-sm font-medium transition-all duration-200 ${
                      currentPage === totalPages
                        ? 'border-white/20 theme-text-muted cursor-not-allowed glass-card-light'
                        : 'theme-button-secondary'
                    }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
      </Box>
    </ThemeBackground>
  );
}

export default Home;